import { BRAND_META, BRAND_ORDER } from '../brand';
import type { Me } from '../types';
import type { ViewKey } from './Header';

/**
 * The icon rail from the command centre mock (4 September 2026).
 *
 * It navigates the SAME ViewKey the top nav does: this is a second way to
 * reach the existing views, never a second router. The mock's Live Atlas
 * item was removed on 1 November 2026 (Matthew: "Live Atlas and Home do the
 * same thing"): the atlas lives on Home, so Home is its one way in.
 *
 * Role gating is the header's rule repeated rather than reinvented: the
 * rail can only offer what the top nav offers, so a role that cannot see
 * a view cannot reach it from here either.
 */
interface RailItem {
  key: string;
  view: ViewKey;
  label: string;
  icon: string;
  logo?: string;
  accent?: string;
  badge?: number;
}

export function Sidebar({
  me,
  view,
  onView,
  pendingApprovals,
}: {
  me: Me;
  view: ViewKey;
  onView: (v: ViewKey) => void;
  pendingApprovals?: number;
}) {
  const showGroup = me.role === 'owner' || me.role === 'analyst';
  const showApprovals = me.role === 'owner' || me.role === 'operator';
  const showChat = me.role === 'owner' || me.role === 'operator' || me.role === 'analyst';
  const brands = BRAND_ORDER.filter((b) => me.brands.includes(b));

  const items: RailItem[] = [
    ...(showGroup ? [{ key: 'home', view: 'group' as ViewKey, label: 'Home', icon: '⌂' }] : []),
    ...(showChat
      ? [{ key: 'afrobot', view: 'chat' as ViewKey, label: 'A.F.R.O.B.O.T', icon: '◎' }]
      : []),
    ...(showApprovals
      ? [
          {
            key: 'approvals',
            view: 'approvals' as ViewKey,
            label: 'Approvals',
            icon: '✓',
            ...(pendingApprovals ? { badge: pendingApprovals } : {}),
          },
        ]
      : []),
    // Board joined the rail on 14 September 2026 when it came off the top
    // nav. Everyone who can see the Shell can see the work: writing is
    // owner and operator and the tool layer enforces that, so hiding the
    // rail entry from an analyst would hide the plan from someone who can
    // already read every number behind it.
    { key: 'board', view: 'board' as ViewKey, label: 'Board', icon: '▤' },
    // The full reference behind the Board's "Coming up" panel. Read-only
    // for every role: it is a document in the repo, not platform data.
    { key: 'calendar', view: 'calendar' as ViewKey, label: 'Calendar', icon: '▦' },
    { key: 'competitors', view: 'competitors', label: 'Competitors', icon: '≣' },
    ...brands.map((b) => ({
      key: b,
      view: b as ViewKey,
      label: BRAND_META[b].label,
      icon: BRAND_META[b].label.charAt(0),
      // The real mark, not an initial. CashBlack ships a vector in the
      // design assets; Afrofiliate and EqualiShop have none in the repo,
      // so theirs are the profile pictures of the Instagram accounts we
      // own, read from SocialAPI (4 September 2026). Swap in a vector
      // the day one exists.
      logo: b === 'cashblack' ? '/brands/cashblack.svg' : `/brands/${b}.jpg`,
      accent: BRAND_META[b].series,
    })),
  ];

  return (
    <aside className="cc-rail" aria-label="Sections">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`cc-rail-btn${view === item.view ? ' active' : ''}`}
          aria-current={view === item.view ? 'page' : undefined}
          style={item.accent ? ({ ['--rail-accent' as string]: item.accent }) : undefined}
          onClick={() => onView(item.view)}
        >
          {item.logo ? (
            <img className="cc-rail-logo" src={item.logo} alt="" width={22} height={22} />
          ) : (
            <i aria-hidden>{item.icon}</i>
          )}
          <span>{item.label}</span>
          {item.badge ? (
            <b className="cc-rail-badge" aria-label={`${item.badge} waiting`}>
              {item.badge}
            </b>
          ) : null}
        </button>
      ))}
      <div className="cc-rail-footer">
        <strong>A fairer</strong>
        more inclusive
        <br />
        more prosperous
        <br />
        tomorrow.
      </div>
    </aside>
  );
}
